import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { EmpleadoService } from '@services/empleado.service';
import { VacacionesService } from '@services/vacaciones.service';
import { EmpleadoList } from '@schemas/empleado.schema';
import { SolicitudVacaciones, EstatusVacaciones } from '@schemas/vacaciones.schema';
import { getEstatusColor as _getEstatusColor } from '@shared/vacaciones.helpers';

interface ChartItem {
  label: string;
  count: number;
  color: string;
  pct: number;
}

interface ProximaVacacion {
  nombre: string;
  area: string;
  fechaInicio: string;
  fechaFin: string;
  dias: number;
  estatus: EstatusVacaciones;
  /** true si la vacación cubre la fecha de hoy (en curso). */
  enCurso: boolean;
}

interface IngresoReciente {
  nombre: string;
  idInterno: string;
  puesto: string;
  direccion: string;
  fechaIngreso: string;
}

interface VencimientoProximo {
  nombre: string;
  idInterno: string;
  area: string;
  fechaVencimiento: string;
  diasDisponibles: number;
}

const COLORES_DIRECCION = [
  '#1890ff',
  '#52c41a',
  '#fa8c16',
  '#722ed1',
  '#eb2f96',
  '#13c2c2',
  '#fa541c',
  '#2f54eb',
  '#a0d911',
  '#597ef7',
];

import {
  CellStack,
  EmptyState,
  ListRow,
  PageHeader,
  SectionHeader,
  StatCard,
  StatusText,
} from '@intelishs-group/ui';

@Component({
  selector: 'app-dashboard',
  imports: [
    DatePipe,
    NzButtonModule,
    NzIconModule,
    NzTooltipModule,
    CellStack,
    ListRow,
    PageHeader,
    SectionHeader,
    StatCard,
    StatusText,
    EmptyState,
  ],
  templateUrl: './dashboard.page.html',
  styleUrl: './dashboard.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPage implements OnInit {
  private readonly router = inject(Router);
  private readonly empleadoService = inject(EmpleadoService);
  private readonly vacacionesService = inject(VacacionesService);
  private readonly destroyRef = inject(DestroyRef);

  // ── Stats principales ────────────────────────────────────────────
  totalEmpleados = signal(0);
  empleadosActivos = signal(0);
  empleadosInactivos = signal(0);

  // ── Vacaciones KPIs ──────────────────────────────────────────────
  pendientesDirector = signal(0);
  pendientesRH = signal(0);
  totalPendientes = computed(() => this.pendientesDirector() + this.pendientesRH());
  empleadosEnVacaciones = signal(0);
  proximasVacaciones7d = signal(0);

  // ── Gráficas ─────────────────────────────────────────────────────
  empleadosPorDireccion = signal<ChartItem[]>([]);

  // Vacaciones por mes — reactivas al año seleccionado.
  // Todas las solicitudes vienen en una sola llamada `getAll()` y se
  // guardan acá; cambiar de año es solo un recompute en memoria.
  private solicitudesData = signal<SolicitudVacaciones[]>([]);
  anioSeleccionado = signal<number>(new Date().getFullYear());

  /** Años con al menos una solicitud (por fechaInicio), ordenados asc. */
  aniosDisponibles = computed<number[]>(() => {
    const set = new Set<number>();
    for (const s of this.solicitudesData()) {
      const y = new Date(s.fechaInicio).getFullYear();
      if (!isNaN(y)) set.add(y);
    }
    // Asegura que el año actual aparezca aunque no haya solicitudes.
    set.add(new Date().getFullYear());
    return [...set].sort((a, b) => a - b);
  });

  puedeIrAtras = computed(() => {
    const anios = this.aniosDisponibles();
    return anios.length > 0 && this.anioSeleccionado() > anios[0];
  });

  puedeIrAdelante = computed(() => {
    const anios = this.aniosDisponibles();
    return anios.length > 0 && this.anioSeleccionado() < anios[anios.length - 1];
  });

  totalSolicitudesAnio = computed(
    () =>
      this.solicitudesData().filter(
        (s) => new Date(s.fechaSolicitud).getFullYear() === this.anioSeleccionado(),
      ).length,
  );

  vacacionesPorMes = computed(() =>
    this.calcularPorMes(this.solicitudesData(), this.anioSeleccionado()),
  );

  // ── Listas ───────────────────────────────────────────────────────
  proximasVacaciones = signal<ProximaVacacion[]>([]);
  ingresosRecientes = signal<IngresoReciente[]>([]);
  vencimientosProximos = signal<VencimientoProximo[]>([]);

  ngOnInit(): void {
    this.cargarEmpleados();
    this.cargarVacaciones();
  }

  // ── Navegación ───────────────────────────────────────────────────
  irA(ruta: string): void {
    this.router.navigate([ruta]);
  }

  getEstatusColor = _getEstatusColor;

  // ── Carga de datos ───────────────────────────────────────────────
  private cargarEmpleados(): void {
    this.empleadoService
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.totalEmpleados.set(data.length);
          const activos = data.filter((e) => e.activo);
          this.empleadosActivos.set(activos.length);
          this.empleadosInactivos.set(data.length - activos.length);
          this.calcularPorDireccion(activos);
          this.calcularIngresosRecientes(activos);
          this.cargarVencimientos(activos.map((e) => e.id));
        },
        error: () => {
          // En caso de error la vista queda en 0 sin crash
        },
      });
  }

  /**
   * Vencimientos próximos: pide el saldo del equipo (todos los activos)
   * y filtra los que tengan periodoAnterior con días disponibles > 0 y
   * fechaVencimiento dentro de los próximos 30 días. Una sola llamada.
   */
  private cargarVencimientos(empleadoIds: number[]): void {
    if (empleadoIds.length === 0) return;
    this.vacacionesService
      .getEquipoSaldo({ empleadoIds })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (equipo) => {
          const hoy = new Date();
          hoy.setHours(0, 0, 0, 0);
          const en30 = new Date(hoy);
          en30.setDate(en30.getDate() + 30);

          const lista: VencimientoProximo[] = equipo
            .filter((e) => {
              const pa = e.periodoAnterior;
              if (!pa || pa.diasDisponibles <= 0) return false;
              const venc = new Date(pa.fechaVencimiento);
              return !isNaN(venc.getTime()) && venc >= hoy && venc <= en30;
            })
            .sort(
              (a, b) =>
                new Date(a.periodoAnterior!.fechaVencimiento).getTime() -
                new Date(b.periodoAnterior!.fechaVencimiento).getTime(),
            )
            .slice(0, 6)
            .map((e) => ({
              nombre: e.empleadoNombre,
              idInterno: e.idInterno,
              area: e.area || '—',
              fechaVencimiento: e.periodoAnterior!.fechaVencimiento,
              diasDisponibles: Math.round(e.periodoAnterior!.diasDisponibles * 10) / 10,
            }));
          this.vencimientosProximos.set(lista);
        },
        error: () => {
          // sin crash: la card se queda en empty-state.
        },
      });
  }

  private cargarVacaciones(): void {
    this.vacacionesService
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const hoy = new Date();
          hoy.setHours(0, 0, 0, 0);
          const en7dias = new Date(hoy);
          en7dias.setDate(en7dias.getDate() + 7);
          const en30dias = new Date(hoy);
          en30dias.setDate(en30dias.getDate() + 30);

          this.pendientesDirector.set(data.filter((s) => s.estatus === 'Pendiente').length);
          this.pendientesRH.set(data.filter((s) => s.estatus === 'Aprobada Director').length);
          this.empleadosEnVacaciones.set(
            data.filter(
              (s) =>
                s.estatus === 'Aprobada' &&
                new Date(s.fechaInicio) <= hoy &&
                new Date(s.fechaFin) >= hoy,
            ).length,
          );
          this.proximasVacaciones7d.set(
            data.filter(
              (s) =>
                s.estatus === 'Aprobada' &&
                new Date(s.fechaInicio) > hoy &&
                new Date(s.fechaInicio) <= en7dias,
            ).length,
          );
          // Los computeds `vacacionesPorMes`, `totalSolicitudesAnio` y
          // `aniosDisponibles` se recalculan automáticamente al setear esto.
          this.solicitudesData.set(data);
          this.calcularProximasVacaciones(data, hoy, en30dias);
        },
        error: () => {
          // En caso de error la vista queda en 0 sin crash
        },
      });
  }

  // ── Navegación de año en "Vacaciones por Mes" ───────────────────
  irAnioAnterior(): void {
    if (this.puedeIrAtras()) this.anioSeleccionado.update((y) => y - 1);
  }

  irAnioSiguiente(): void {
    if (this.puedeIrAdelante()) this.anioSeleccionado.update((y) => y + 1);
  }

  // ── Cálculos para gráficas ───────────────────────────────────────
  private calcularPorDireccion(empleados: EmpleadoList[]): void {
    const conteo = new Map<string, number>();
    for (const e of empleados) {
      const dir = e.direccion || 'Sin dirección';
      conteo.set(dir, (conteo.get(dir) ?? 0) + 1);
    }
    const items: ChartItem[] = [...conteo.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count], i) => ({
        label,
        count,
        color: COLORES_DIRECCION[i % COLORES_DIRECCION.length],
        pct: 0,
      }));

    const max = Math.max(...items.map((i) => i.count), 1);
    items.forEach((item) => (item.pct = Math.round((item.count / max) * 100)));
    this.empleadosPorDireccion.set(items);
  }

  /**
   * Función pura — cuenta solicitudes activas por mes de inicio para el
   * año dado. La consume el computed `vacacionesPorMes`.
   */
  private calcularPorMes(
    data: SolicitudVacaciones[],
    anio: number,
  ): { mes: string; count: number; pct: number }[] {
    const meses = [
      'Ene',
      'Feb',
      'Mar',
      'Abr',
      'May',
      'Jun',
      'Jul',
      'Ago',
      'Sep',
      'Oct',
      'Nov',
      'Dic',
    ];
    const conteo = new Array(12).fill(0);

    for (const s of data) {
      if (
        (s.estatus === 'Aprobada' ||
          s.estatus === 'Aprobada Director' ||
          s.estatus === 'Pendiente') &&
        new Date(s.fechaInicio).getFullYear() === anio
      ) {
        conteo[new Date(s.fechaInicio).getMonth()]++;
      }
    }

    const max = Math.max(...conteo, 1);
    return meses.map((mes, i) => ({
      mes,
      count: conteo[i],
      pct: Math.round((conteo[i] / max) * 100),
    }));
  }

  private calcularProximasVacaciones(data: SolicitudVacaciones[], hoy: Date, en30dias: Date): void {
    // Incluye dos casos:
    //   • "En curso": vacación aprobada cuyo rango cubre hoy.
    //   • "Próximas": vacación aprobada que inicia dentro de 30 días.
    // Las en-curso van arriba (ordenadas por fechaFin asc — sale primero
    // quien regresa antes); las próximas van por fechaInicio asc.
    const enCurso = data
      .filter(
        (s) =>
          s.estatus === 'Aprobada' && new Date(s.fechaInicio) <= hoy && new Date(s.fechaFin) >= hoy,
      )
      .sort((a, b) => new Date(a.fechaFin).getTime() - new Date(b.fechaFin).getTime())
      .map((s) => this.toProximaVacacion(s, true));

    const futuras = data
      .filter(
        (s) =>
          s.estatus === 'Aprobada' &&
          new Date(s.fechaInicio) > hoy &&
          new Date(s.fechaInicio) <= en30dias,
      )
      .sort((a, b) => new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime())
      .map((s) => this.toProximaVacacion(s, false));

    this.proximasVacaciones.set([...enCurso, ...futuras].slice(0, 8));
  }

  private toProximaVacacion(s: SolicitudVacaciones, enCurso: boolean): ProximaVacacion {
    return {
      nombre: s.empleadoNombre,
      area: s.area ?? '—',
      fechaInicio: s.fechaInicio,
      fechaFin: s.fechaFin,
      dias: s.diasSolicitados,
      estatus: s.estatus,
      enCurso,
    };
  }

  private calcularIngresosRecientes(empleados: EmpleadoList[]): void {
    const recientes = empleados
      .filter((e) => e.fechaIngreso)
      .sort((a, b) => new Date(b.fechaIngreso!).getTime() - new Date(a.fechaIngreso!).getTime())
      .slice(0, 5)
      .map((e) => ({
        nombre: e.nombreCompleto,
        idInterno: e.idInterno,
        puesto: e.puesto ?? '—',
        direccion: e.direccion ?? '—',
        fechaIngreso: e.fechaIngreso!,
      }));
    this.ingresosRecientes.set(recientes);
  }
}
